import { useEffect, useState } from 'react';

import { Box, Icon, IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreFilled, IconSettingsFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import AboutAddressEditsModal from '../../modals/AboutAddressEditsModal';
import AddPropertyModal from '../../modals/AddPropertyModal';
import ArchivePropertyModal from '../../modals/ArchivePropertyModal';
import CloneThisListingConversionModal from '../../modals/CloneThisListingConversionModal';
import EditPropertyTypeModal from '../../modals/EditPropertyTypeModal';
import MapPinAdjustmentRequestModal from '../../modals/MapPinAdjustmentRequestModal';

import { emitClickReportMapPinLocationFromPropertyDashboardEvent } from '../../../analytics/events/correctMapPin';
import { useAuth } from '../../AuthProvider';

type Props = {
  listingAlias: string;
};

const EditPropertyTypeMenu = ({ listingAlias }: Props) => {
  const { user } = useAuth();
  const isPostingPathAdmin = user?.isPostingPathAdmin ?? false;

  const { data: overviewData, isPending } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { isArchived, isMultifamily, canSubmitFixMapPinRequest, address } = overviewData ?? {};

  const [showAboutAddressModal, setShowAboutAddressModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showMapPinModal, setShowMapPinModal] = useState(false);
  const [showEditPropertyTypeModal, setShowEditPropertyTypeModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [addPropertyModalConfig, setAddPropertyModalConfig] = useState<{ isOpen: boolean; behavior?: 'Edit' | 'Add' }>({
    isOpen: false,
  });

  useEffect(
    () => () => {
      setShowEditPropertyTypeModal(false);
      setAddPropertyModalConfig({ isOpen: false });
      setShowArchiveModal(false);
      setShowMapPinModal(false);
      setShowAboutAddressModal(false);
      setShowCloneModal(false);
    },
    [],
  );

  const handleArchiveClick = () => setShowArchiveModal(true);
  const handleAboutAddressEditsClick = () => setShowAboutAddressModal(true);
  const handleEditPropertyTypeClick = () => setShowEditPropertyTypeModal(true);
  const handleAdminToolsClick = () => setAddPropertyModalConfig({ isOpen: true, behavior: 'Edit' });

  const handleReportMapPinLocationClick = () => {
    if (user && overviewData) {
      emitClickReportMapPinLocationFromPropertyDashboardEvent({
        user,
        currentProperty: { listingAlias, ...address },
      });
    }
    setShowMapPinModal(true);
  };

  const { formattedAddressWithoutUnit } = propertyUtils.formatAddress(address ?? {});

  return (
    <>
      <Menu
        data-testid="EditPropertyTypeMenu"
        content={
          <Box css={{ zIndex: 'popover' }}>
            <Menu.Item onClick={handleArchiveClick}>{`${isArchived ? 'Unarchive' : 'Archive'} property`}</Menu.Item>
            {Boolean(canSubmitFixMapPinRequest) && (
              <Menu.Item onClick={handleReportMapPinLocationClick}>Report map pin location</Menu.Item>
            )}
            {!isArchived && !isMultifamily && (
              <>
                <Menu.Item onClick={handleAboutAddressEditsClick}>About address edits</Menu.Item>
                <Menu.Item onClick={handleEditPropertyTypeClick}>Edit property type</Menu.Item>
              </>
            )}
            {isPostingPathAdmin && (
              <Menu.Item onClick={handleAdminToolsClick}>
                <Menu.ItemIcon render={<IconSettingsFilled />} />
                <Menu.ItemLabel css={{ color: 'text.accent.red.strong' }}>Admin tools</Menu.ItemLabel>
              </Menu.Item>
            )}
          </Box>
        }
        trigger={
          <IconButton emphasis="bare" tone="brand" title="Property options" disabled={isPending}>
            <Icon>
              <IconMoreFilled />
            </Icon>
          </IconButton>
        }
      />

      {showArchiveModal && address && (
        <ArchivePropertyModal
          onClose={() => setShowArchiveModal(false)}
          isArchived={Boolean(isArchived)}
          listingAlias={listingAlias}
          address={address}
        />
      )}

      {showAboutAddressModal && (
        <AboutAddressEditsModal
          listingAlias={listingAlias}
          onClose={() => setShowAboutAddressModal(false)}
          onCloneClick={() => setShowCloneModal(true)}
          setAddPropertyModalConfig={setAddPropertyModalConfig}
        />
      )}

      {showEditPropertyTypeModal && (
        <EditPropertyTypeModal listingAlias={listingAlias} onClose={() => setShowEditPropertyTypeModal(false)} />
      )}

      {showCloneModal && (
        <CloneThisListingConversionModal
          listingAlias={listingAlias}
          unit={address?.unit}
          formattedAddressWithoutUnit={formattedAddressWithoutUnit}
          onClose={() => setShowCloneModal(false)}
        />
      )}

      {showMapPinModal && (
        <MapPinAdjustmentRequestModal listingAlias={listingAlias} onClose={() => setShowMapPinModal(false)} />
      )}

      <AddPropertyModal
        isOpen={addPropertyModalConfig.isOpen}
        behavior={addPropertyModalConfig.behavior}
        onClose={() => setAddPropertyModalConfig({ isOpen: false })}
      />
    </>
  );
};

export default EditPropertyTypeMenu;
